import { clamp, round } from '@/utils/mathUtils';
import { Icon } from '@iconify/react';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

export function BaseNumberInput({
  value,
  onChange,
  step = 1,
  min = 0,
  max = Infinity,
}: {
  value: number;
  onChange: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current!.value = String(value);
  }, [value]);

  return (
    <input
      className="w-full appearance-none p-1"
      ref={inputRef}
      type="text"
      defaultValue={value}
      step={step}
      min={min}
      max={max}
      onChange={e => {
        const newValue = Number(e.target.value);
        if (Number.isNaN(newValue)) {
          inputRef.current!.value = String(value);
          return;
        }
        onChange(newValue);
      }}
    />
  );
}

export function NumberInput({
  className,
  value,
  onChange,
  step = 1,
  min = 0,
  max = Infinity,
  ...props
}: Parameters<typeof BaseNumberInput>[0] & {
  className?: string;
}) {
  const increment = useCallback(() => {
    onChange(round(clamp(value + step, min, max), step));
  }, [onChange, value, step, min, max]);
  const decrement = useCallback(() => {
    onChange(round(clamp(value - step, min, max), step));
  }, [onChange, value, step, min, max]);
  return (
    <div
      className={clsx(
        'flex w-20 items-center rounded border border-gray-300',
        className,
      )}
    >
      <BaseNumberInput value={value} onChange={onChange} {...props} />
      <div className="grid h-full grid-rows-2 border-l border-gray-300">
        <button
          type="button"
          className="cursor-pointer border-b border-gray-300 hover:bg-gray-100"
          onClick={increment}
        >
          <Icon icon="fluent:chevron-up-20-regular" />
        </button>
        <button
          type="button"
          className="cursor-pointer hover:bg-gray-100"
          onClick={decrement}
        >
          <Icon icon="fluent:chevron-down-20-regular" />
        </button>
      </div>
    </div>
  );
}
